import React, { useState, useCallback } from 'react';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import BixCheckbox from '$/components/global/bixCheckbox';
import './MarkClientsAsReceivedModal.scss';

export interface MarkClientsAsReceivedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMarkAsReceived: (clientIds: string[], onStartFetching: () => void) => Promise<void>;
  bixbyId: string;
  dataSiteName: string;
  provider: string;
  docNames: string[];
  missingClients: Array<{ id: string; name: string }>;
}

const MarkClientsAsReceivedModal: React.FC<MarkClientsAsReceivedModalProps> = ({
  isOpen,
  onClose,
  onMarkAsReceived,
  bixbyId,
  dataSiteName,
  provider,
  docNames,
  missingClients,
}) => {
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [markingPhase, setMarkingPhase] = useState<'idle' | 'marking' | 'fetching'>('idle');

  const handleToggleClient = useCallback((clientId: string) => {
    setSelectedClientIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  }, []);

  const handleMarkAsReceived = useCallback(async () => {
    if (selectedClientIds.size === 0) return;

    try {
      setMarkingPhase('marking');
      await onMarkAsReceived(Array.from(selectedClientIds), () => setMarkingPhase('fetching'));
      setSelectedClientIds(new Set());
      onClose();
    } finally {
      setMarkingPhase('idle');
    }
  }, [selectedClientIds, onMarkAsReceived, onClose]);

  const handleClose = useCallback(() => {
    if (markingPhase === 'idle') {
      setSelectedClientIds(new Set());
      onClose();
    }
  }, [markingPhase, onClose]);

  if (!isOpen) return null;

  return (
    <Modal active={isOpen} onClose={handleClose}>
      <div className="mark-clients-as-received-modal">
        <div className="modal-context-info">
          <div className="context-item">
            <span className="context-label">Bixby ID:</span>
            <span className="context-value">{bixbyId}</span>
          </div>
          <div className="context-item">
            <span className="context-label">Provider:</span>
            <span className="context-value">{provider}</span>
          </div>
          <div className="context-item">
            <span className="context-label">Data Site:</span>
            <span className="context-value">{dataSiteName}</span>
          </div>
          <div className="context-item context-item-documents">
            <span className="context-label">Documents:</span>
            <div className="context-value-documents">
              {docNames.map((doc, index) => (
                <span key={index} className="document-pill">
                  {doc}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-clients-section">
          <h4 className="clients-header">Select Missing Clients to Mark as Received</h4>
          <div className="clients-list">
            {missingClients.map(client => (
              <div key={client.id} className="client-item" onClick={() => handleToggleClient(client.id)}>
                <BixCheckbox
                  id={`client-${client.id}`}
                  checked={selectedClientIds.has(client.id)}
                  onChange={() => handleToggleClient(client.id)}>
                  {client.name}
                </BixCheckbox>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-button-container">
          <BixButton
            type="button"
            onClick={handleMarkAsReceived}
            useLoading
            isLoading={markingPhase !== 'idle'}
            disabled={markingPhase !== 'idle' || selectedClientIds.size === 0}>
            {markingPhase === 'fetching'
              ? 'Fetching Rows...'
              : markingPhase === 'marking'
              ? 'Marking as Received...'
              : 'Mark as Received'}
          </BixButton>
          <BixButton light type="button" onClick={handleClose} disabled={markingPhase !== 'idle'}>
            Cancel
          </BixButton>
        </div>
      </div>
    </Modal>
  );
};

export default MarkClientsAsReceivedModal;
